import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, CheckCircle, Trophy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../config';

function ChallengeBar({ progress, goal, color }) {
  const pct = Math.min(100, Math.round((progress / goal) * 100));
  return (
    <div className="mt-2">
      <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
        <span>{progress} / {goal} completed</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ background: color }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}

export default function ChallengeWidget({ onXpGained }) {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);

  const fetchChallenges = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/challenges`, { withCredentials: true });
      setChallenges(res.data || []);
    } catch (e) {
      if (e.response?.status !== 401) console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const handleClaim = async (challenge) => {
    setClaiming(challenge.challenge_id);
    try {
      const res = await axios.post(
        `${API}/challenges/${challenge.challenge_id}/claim`,
        {},
        { withCredentials: true }
      );
      toast.success(`+${res.data.xp_earned} XP claimed! 🎉`);
      if (onXpGained) onXpGained(res.data.xp_earned);
      // Mark as claimed locally
      setChallenges(prev =>
        prev.map(c =>
          c.challenge_id === challenge.challenge_id ? { ...c, claimed: true } : c
        )
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not claim challenge');
    } finally {
      setClaiming(null);
    }
  };

  const completedCount = challenges.filter(c => c.completed && c.claimed).length;
  const totalXP = challenges.reduce((sum, c) => sum + (c.claimed ? c.xp_reward : 0), 0);

  return (
    <Card className="fly-card p-4" data-testid="challenge-widget">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-[#FF4500]" />
        <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>
          Weekly Challenges
        </h3>
        <span className="ml-auto text-[10px] text-muted-foreground font-medium">
          Resets Monday
        </span>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      ) : challenges.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">
          No active challenges right now. Check back Monday!
        </p>
      ) : (
        <div className="space-y-2.5">
          <AnimatePresence>
            {challenges.map((ch, i) => (
              <motion.div
                key={ch.challenge_id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07 }}
              >
                <div
                  className={`rounded-xl p-3 border transition-all ${
                    ch.claimed
                      ? 'border-[#10B981]/30 bg-[#10B981]/5'
                      : ch.completed
                      ? 'border-[#FF4500]/30 bg-[#FF4500]/5'
                      : 'border-border bg-muted/20'
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0"
                      style={{ background: `${ch.color}18` }}
                    >
                      {ch.claimed ? <CheckCircle className="w-4 h-4 text-[#10B981]" /> : ch.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1.5">
                        <p className={`text-xs font-semibold leading-tight ${ch.claimed ? 'text-muted-foreground line-through' : ''}`}>
                          {ch.title}
                        </p>
                        <span className="text-[10px] font-bold text-[#FF4500] flex-shrink-0">
                          +{ch.xp_reward} XP
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{ch.description}</p>
                      {!ch.claimed && (
                        <ChallengeBar
                          progress={ch.progress}
                          goal={ch.goal}
                          color={ch.claimed ? '#10B981' : ch.color}
                        />
                      )}
                    </div>
                  </div>

                  {ch.completed && !ch.claimed && (
                    <Button
                      size="sm"
                      className="w-full mt-2 h-7 text-xs fly-btn-primary"
                      onClick={() => handleClaim(ch)}
                      disabled={claiming === ch.challenge_id}
                      data-testid={`claim-challenge-${ch.challenge_id}`}
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      {claiming === ch.challenge_id ? 'Claiming...' : `Claim +${ch.xp_reward} XP`}
                    </Button>
                  )}
                  {ch.claimed && (
                    <div className="flex items-center gap-1 mt-1.5 text-[10px] text-[#10B981] font-medium">
                      <CheckCircle className="w-3 h-3" /> XP Claimed
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {completedCount > 0 && (
            <div className="text-center text-xs text-muted-foreground pt-1">
              {completedCount}/{challenges.length} completed · {totalXP} XP earned this week
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
